import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type { Contactmoment, ContactmomentInvoer, ContactmomentWijziging, GesprekFilter, Paginering } from './typen.ts';

export type Gesprekspartner = 'kandidaat' | 'contactpersoon' | 'bedrijf' | 'vacature';

export const contactmomentSleutels = {
  alle: ['contactmomenten'] as const,
  mijn: (filter: GesprekFilter) => ['contactmomenten', 'mijn', filter] as const,
  van: (soort: Gesprekspartner, id: string, paginering: Paginering) =>
    ['contactmomenten', soort, id, paginering] as const,
  laatste: (kandidaatId: string) => ['contactmomenten', 'laatste', kandidaatId] as const,
};

/**
 * Somt de gesprekken op die de aangemelde gebruiker zelf heeft vastgelegd.
 * @param filter Zoekterm, kanalen, richting, met wie, periode en paginering.
 * @returns De query met de contactmomenten, nieuwste eerst.
 */
export function useMijnContactmomenten(filter: GesprekFilter) {
  return useQuery({
    queryKey: contactmomentSleutels.mijn(filter),
    queryFn: () => uitvoeren(api.GET('/api/v1/contactmomenten/mijn', { params: { query: filter } })),
    placeholderData: keepPreviousData,
  });
}

function haalVan(soort: Gesprekspartner, id: string, paginering: Paginering): Promise<Contactmoment[]> {
  const params = { path: { id }, query: paginering };
  switch (soort) {
    case 'kandidaat':
      return uitvoeren(api.GET('/api/v1/kandidaten/{id}/contactmomenten', { params }));
    case 'contactpersoon':
      return uitvoeren(api.GET('/api/v1/contactpersonen/{id}/contactmomenten', { params }));
    case 'bedrijf':
      return uitvoeren(api.GET('/api/v1/bedrijven/{id}/contactmomenten', { params }));
    case 'vacature':
      return uitvoeren(api.GET('/api/v1/vacatures/{id}/contactmomenten', { params }));
  }
}

/**
 * Somt de gesprekken met een kandidaat of contactpersoon, of rond een bedrijf of vacature op.
 * @param soort Met wie of waarover.
 * @param id Het id van de kandidaat, contactpersoon, bedrijf of vacature.
 * @param paginering Limiet en offset.
 * @returns De query met de contactmomenten, nieuwste eerst.
 */
export function useContactmomentenVan(soort: Gesprekspartner, id: string | undefined, paginering: Paginering) {
  return useQuery({
    queryKey: contactmomentSleutels.van(soort, id ?? '', paginering),
    queryFn: () => haalVan(soort, id ?? '', paginering),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

/**
 * Haalt het laatste gesprek met een kandidaat op.
 * @param kandidaatId Het id van de kandidaat.
 * @returns De query met het laatste contactmoment, of `null`.
 */
export function useLaatsteContactmoment(kandidaatId: string | undefined) {
  return useQuery({
    queryKey: contactmomentSleutels.laatste(kandidaatId ?? ''),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/kandidaten/{id}/laatste-contactmoment', { params: { path: { id: kandidaatId ?? '' } } }),
      ),
    enabled: Boolean(kandidaatId),
  });
}

/**
 * De mutaties op contactmomenten: vastleggen, wijzigen en verwijderen.
 * @returns De mutaties.
 */
export function useContactmomentMutaties() {
  const verversen = () => [contactmomentSleutels.alle];
  return {
    legVast: useApiMutatie(
      (invoer: ContactmomentInvoer) => uitvoeren(api.POST('/api/v1/contactmomenten', { body: invoer })),
      verversen,
    ),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: ContactmomentWijziging }) =>
        uitvoeren(api.PATCH('/api/v1/contactmomenten/{id}', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/contactmomenten/{id}', { params: { path: { id } } })),
      verversen,
    ),
  };
}
